import { z } from 'zod';
import { allowed } from '../permissions/can.ts';
import type { Level, Module } from '../permissions/modules.ts';
import type { Principal } from '../permissions/can.ts';

/**
 * What the assistant is allowed to ask for (SCRUM-223, Phase A).
 *
 * One catalog rather than a prompt listing capabilities in prose, for the
 * same reason the notification events are a catalog: the two questions a
 * tool raises — "may this person use it" and "what did it do" — are
 * unanswerable if the set is implicit.
 *
 * ## The rule this file exists to enforce
 *
 * **Every tool runs as the signed-in person, through the same security
 * rules as every screen.** The agent loop lives in the browser and
 * executes each tool through the ordinary `DataPort`, so Firestore refuses
 * the assistant exactly as it refuses them. The `module` and `level` here
 * are *not* the enforcement — they decide which tools the model is told
 * about at all, so it does not spend a turn asking for something that will
 * be refused, and so a refusal reads as "you cannot see that" rather than
 * as a broken tool.
 *
 * The alternative — a server-side agent reading with admin credentials and
 * deciding what to show — was rejected deliberately. It makes the
 * assistant a second authorisation path that has to re-implement every
 * rule in this product, and the first time the two disagree somebody sees
 * margin they have never been allowed to see.
 *
 * ## Why every tool here is read-only
 *
 * Phase A. A write has to be a **proposal the person confirms**, showing
 * exactly what would change, and that is its own piece of work — see
 * SCRUM-223. `writes` is on the interface already so that a tool which
 * acts cannot be added without saying so.
 */
export interface AssistantTool {
  /** Stable, sent to the model and recorded in the usage log. */
  readonly key: string;
  /** What a person would call it, for the settings screen and the log. */
  readonly label: string;
  /** What the model is told. Written for the model, not for a developer. */
  readonly description: string;
  /** The module a caller must hold to be offered this tool. */
  readonly module: Module | null;
  readonly level: Level;
  readonly input: z.ZodTypeAny;
  /**
   * The same shape as JSON Schema, which is what the model is sent.
   *
   * Carried beside the Zod schema rather than generated from it. A
   * converter would be a small pile of version-sensitive introspection
   * for seven fixed shapes, and the risk it is meant to remove —
   * the two drifting apart — is removed instead by a test that compares
   * the required keys of both.
   *
   * It matters more than it looks: before this existed the model was sent
   * `{ type: 'object' }` with no properties, so it was told each tool took
   * an object and had to **guess the field names**. `get_schedule` wants
   * `jobId`; a model guessing `job_id` gets a refusal, tries again, and
   * burns the allowance discovering an argument name.
   */
  readonly inputSchema: Record<string, unknown>;
  /**
   * Whether running it changes anything.
   *
   * Every Phase A tool is `false`. A `true` here must come with a
   * confirmation step: the model proposes, the person confirms, and the
   * browser then calls the same use case the button would.
   */
  readonly writes: boolean;
}

const DateRange = z.object({
  /** ISO `YYYY-MM-DD`. */
  from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
});

export const ASSISTANT_TOOLS: readonly AssistantTool[] = [
  {
    key: 'list_jobs',
    label: 'List jobs',
    description:
      'List the jobs in this company, with their status, address and dates. Use this to find a job id before asking about its schedule or its work.',
    module: 'jobs',
    level: 'view',
    input: z.object({
      status: z.enum(['lead', 'open', 'closed', 'archived']).optional(),
      search: z.string().max(200).optional(),
    }),
    inputSchema: {
      type: 'object',
      properties: {
        status: {
          type: 'string',
          enum: ['lead', 'open', 'closed', 'archived'],
          description: 'Only jobs in this state.',
        },
        search: { type: 'string', description: 'Only jobs whose name contains this.' },
      },
      required: [],
    },
    writes: false,
  },
  {
    key: 'get_schedule',
    label: 'Read a job’s schedule',
    description:
      "Read one job's schedule items: titles, start and end dates, how far along each is, and who is assigned. Takes a job id from list_jobs.",
    module: 'schedule',
    level: 'view',
    input: z.object({ jobId: z.string().min(1), range: DateRange.optional() }),
    inputSchema: {
      type: 'object',
      properties: {
        jobId: { type: 'string', description: 'From list_jobs.' },
        range: {
          type: 'object',
          description: 'Only work overlapping these dates.',
          properties: {
            from: { type: 'string', description: 'YYYY-MM-DD' },
            to: { type: 'string', description: 'YYYY-MM-DD' },
          },
          required: ['from', 'to'],
        },
      },
      required: ['jobId'],
    },
    writes: false,
  },
  {
    key: 'equipment_in_use',
    label: 'Find where equipment is',
    description:
      'Across every job at once, find which machines are booked over a range of dates, and which are double-booked. Use this for questions like "what equipment is being used next Monday" or "is the excavator free next week".',
    module: 'equipment',
    level: 'view',
    input: z.object({ range: DateRange, equipmentId: z.string().min(1).optional() }),
    inputSchema: {
      type: 'object',
      properties: {
        range: {
          type: 'object',
          description: 'The days to ask about. One day is from and to the same date.',
          properties: {
            from: { type: 'string', description: 'YYYY-MM-DD' },
            to: { type: 'string', description: 'YYYY-MM-DD' },
          },
          required: ['from', 'to'],
        },
        equipmentId: {
          type: 'string',
          description: 'From list_equipment, to ask about one machine.',
        },
      },
      required: ['range'],
    },
    writes: false,
  },
  {
    key: 'list_equipment',
    label: 'List equipment',
    description:
      'List the company’s plant and tools: unit name, kind of machine, status and when each is next due a service.',
    module: 'equipment',
    level: 'view',
    input: z.object({ search: z.string().max(200).optional() }),
    inputSchema: {
      type: 'object',
      properties: {
        search: { type: 'string', description: 'Only machines whose name contains this.' },
      },
      required: [],
    },
    writes: false,
  },
  {
    key: 'list_todos',
    label: 'List to-dos',
    description:
      'List to-dos on a job, with who they are for and when they are due. Takes a job id from list_jobs.',
    module: 'todos',
    level: 'view',
    input: z.object({ jobId: z.string().min(1), openOnly: z.boolean().optional() }),
    inputSchema: {
      type: 'object',
      properties: {
        jobId: { type: 'string', description: 'From list_jobs.' },
        openOnly: { type: 'boolean', description: 'Leave out anything already done.' },
      },
      required: ['jobId'],
    },
    writes: false,
  },
  {
    key: 'list_daily_logs',
    label: 'Read daily logs',
    description:
      'Read the daily logs on a job — what happened on site, day by day. Takes a job id from list_jobs.',
    module: 'dailyLogs',
    level: 'view',
    input: z.object({ jobId: z.string().min(1), range: DateRange.optional() }),
    inputSchema: {
      type: 'object',
      properties: {
        jobId: { type: 'string', description: 'From list_jobs.' },
        range: {
          type: 'object',
          description: 'Only logs on these dates.',
          properties: {
            from: { type: 'string', description: 'YYYY-MM-DD' },
            to: { type: 'string', description: 'YYYY-MM-DD' },
          },
          required: ['from', 'to'],
        },
      },
      required: ['jobId'],
    },
    writes: false,
  },
  {
    key: 'propose_shift_work',
    label: 'Propose moving work',
    description:
      'Work out what moving a job\u2019s work earlier or later would do, and show it for approval. This does NOT change anything: it returns the activities that would move and their new dates, and the person decides. Use it whenever somebody asks to push work back or pull it forward. Say which scope you chose and why, because "add three days to this job" could mean every remaining activity or one phase.',
    module: 'schedule',
    // `edit`, because although this writes nothing, offering it to
    // somebody who could never confirm it wastes their time and ends in a
    // refusal they cannot act on.
    level: 'edit',
    input: z.object({
      jobId: z.string().min(1),
      days: z.number().int(),
      scope: z.enum(['remaining', 'all', 'phase']),
      phaseId: z.string().min(1).optional(),
    }),
    inputSchema: {
      type: 'object',
      properties: {
        jobId: { type: 'string', description: 'From list_jobs.' },
        days: {
          type: 'number',
          description: 'Work days to move by. Negative pulls the work earlier.',
        },
        scope: {
          type: 'string',
          enum: ['remaining', 'all', 'phase'],
          description:
            'remaining: work that has not started, which is what "push the job back" usually means. all: every unfinished activity. phase: one phase, and then phaseId is required.',
        },
        phaseId: { type: 'string', description: 'Required when scope is phase.' },
      },
      required: ['jobId', 'days', 'scope'],
    },
    /**
     * **True, and it still writes nothing.**
     *
     * The flag means "this is about changing something", which is what
     * decides that the answer has to be confirmed. `runAssistantTool`
     * refuses any tool that would write directly; this one returns a
     * proposal and the person's confirmation runs the use case.
     */
    writes: true,
  },
  {
    key: 'explain_feature',
    label: 'Explain how Buildmate works',
    description:
      'Explain how a part of Buildmate works — what a module is for, how a workflow runs, what a term means. Use this whenever somebody asks how to do something rather than asking about their own data.',
    /**
     * No module, deliberately.
     *
     * This is the half that makes the assistant a tutorial, and gating
     * product documentation behind a permission would mean the people most
     * likely to need it — somebody new, on the smallest role — are the
     * ones who cannot ask. It reads `docs/features`, which describes the
     * product and contains nobody's data.
     */
    module: null,
    level: 'view',
    input: z.object({ topic: z.string().min(1).max(200) }),
    inputSchema: {
      type: 'object',
      properties: {
        topic: {
          type: 'string',
          description: 'What to explain: a module, a workflow or a term.',
        },
      },
      required: ['topic'],
    },
    writes: false,
  },
];

const BY_KEY = new Map(ASSISTANT_TOOLS.map((tool) => [tool.key, tool]));

export function assistantTool(key: string): AssistantTool | undefined {
  return BY_KEY.get(key);
}

/**
 * The tools this person may use.
 *
 * Filtered before the model is told about them, so it never proposes a
 * tool whose result would be a permission error — which reads as the
 * assistant being broken rather than as the person not having access.
 *
 * This is **not** the security boundary. Firestore is. If this filter were
 * removed entirely, every tool would still be executed through the
 * person's own session and refused on exactly the same documents.
 */
export function toolsFor(principal: Principal | null): AssistantTool[] {
  if (!principal) return [];
  return ASSISTANT_TOOLS.filter(
    (tool) => tool.module === null || allowed(principal, tool.module, tool.level),
  );
}
